import { Badge, Flex, Text } from '@chakra-ui/react';
import {
  InquiryWithContext,
  SupplierInquiry,
  TierPrice,
} from '@texas/api/endpoints/inquiryApi';
import { ApiRequestType } from '@texas/api/hooks/useApiRequest';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { useTranslation } from 'react-i18next';

type InquiryPreviewSource = Pick<
  InquiryWithContext | SupplierInquiry,
  'id' | 'formattedInquiryNumber' | 'tierPrices'
> & {
  controlledInquiries?: InquiryWithContext[];
};

interface Props {
  inquiry: InquiryPreviewSource;
  action: 'accept' | 'reject';
  isOpen: boolean;
  onClose: () => void;
  onPerformed: () => void;
  primaryRequest: ApiRequestType<[number], SupplierInquiry>;
}

interface InquiryPricePreview {
  inquiryNumber: string;
  tierPrices: TierPrice[];
}

export function InquiryStatusDialog({
  inquiry,
  action,
  isOpen,
  onClose,
  onPerformed,
  primaryRequest,
}: Props) {
  const { t } = useTranslation();
  const previews = getPricePreviews(inquiry);
  const priceCount = previews.reduce(
    (sum, preview) => sum + preview.tierPrices.length,
    0,
  );
  const isAccept = action === 'accept';
  const canAccept = canAcceptInquiry(inquiry);

  return (
    <VerifyDialogWithRequest
      headerTitle={t(
        isAccept ? 'inquiry.acceptInquiry' : 'inquiry.rejectInquiry',
      )}
      secondaryButtonTitle={t('general.cancel')}
      primaryButtonTitle={t(isAccept ? 'inquiry.accept' : 'inquiry.reject')}
      primaryRequest={primaryRequest}
      args={[inquiry.id]}
      isOpen={isOpen}
      onClose={onClose}
      onPerformed={onPerformed}
      onSuccessTitle={t(
        isAccept ? 'inquiry.acceptedPrices' : 'inquiry.rejectedPrices',
      )}
      primaryButtonDisabled={isAccept && !canAccept}
    >
      <Flex direction="column" gap={3}>
        <Text>
          {t(
            isAccept
              ? 'inquiry.acceptInquiryConfirm'
              : 'inquiry.rejectInquiryConfirm',
          )}
        </Text>
        {isAccept && !canAccept && (
          <Text fontSize="sm" color="orange.300">
            {t('inquiry.allPricesRequired')}
          </Text>
        )}
        <Flex direction="column" gap={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t('inquiry.priceChangePreview', { count: priceCount })}
          </Text>
          {priceCount > 0 ? (
            previews.map((preview) => (
              <Flex key={preview.inquiryNumber} direction="column" gap={1}>
                <Text fontSize="sm" color="gray.500">
                  {preview.inquiryNumber}
                </Text>
                <Flex gap={2} wrap="wrap">
                  {preview.tierPrices.map((tierPrice) => (
                    <Badge key={tierPrice.id} colorScheme="gray">
                      {t('inquiry.tierPricePreview', {
                        quantity: tierPrice.quantity,
                        price: tierPrice.price,
                      })}
                    </Badge>
                  ))}
                </Flex>
              </Flex>
            ))
          ) : (
            <Text fontSize="sm" color="gray.500">
              {t('inquiry.noPricesToUpdate')}
            </Text>
          )}
        </Flex>
      </Flex>
    </VerifyDialogWithRequest>
  );
}

export function canAcceptInquiry(inquiry: InquiryPreviewSource): boolean {
  return [inquiry, ...(inquiry.controlledInquiries ?? [])].every((item) => {
    const tierPrices = getLatestTierPrices(item.tierPrices);
    return (
      tierPrices.length > 0 &&
      tierPrices.every((tierPrice) => tierPrice.price != null)
    );
  });
}

function getPricePreviews(
  inquiry: InquiryPreviewSource,
): InquiryPricePreview[] {
  return [inquiry, ...(inquiry.controlledInquiries ?? [])]
    .map((item) => ({
      inquiryNumber: item.formattedInquiryNumber,
      tierPrices: getLatestTierPrices(item.tierPrices).filter(
        (tierPrice) => tierPrice.price != null,
      ),
    }))
    .filter((preview) => preview.tierPrices.length > 0);
}

function getLatestTierPrices(tierPrices: TierPrice[]): TierPrice[] {
  const supersededIds = new Set(
    tierPrices
      .map((tierPrice) => tierPrice.basedOnId)
      .filter((id): id is number => id != null),
  );

  return tierPrices
    .filter((tierPrice) => !supersededIds.has(tierPrice.id))
    .sort((a, b) => a.quantity - b.quantity);
}
